// Pure generator logic for the CSS/SVG animation + effect cluster. The React
// components own state + live preview; these builders turn state into copyable
// CSS/HTML/SVG so the logic stays testable and framework-free. No DOM APIs are
// used here so the helpers run identically on the server and in tests.

// --- shared ---------------------------------------------------------------

function trimNum(value: number): string {
  return String(Number(value.toFixed(3)));
}

// --- css animation generator ----------------------------------------------

export type AnimationPreset = 'fade' | 'slide' | 'bounce' | 'pulse' | 'spin' | 'shake';

export type AnimationState = {
  preset: AnimationPreset;
  duration: number;
  delay: number;
  timing: string;
  iteration: string;
  direction: string;
};

export const ANIMATION_PRESETS: AnimationPreset[] = [
  'fade',
  'slide',
  'bounce',
  'pulse',
  'spin',
  'shake',
];

export const ANIMATION_PRESET_LABELS: Record<AnimationPreset, string> = {
  fade: 'Fade in',
  slide: 'Slide in',
  bounce: 'Bounce',
  pulse: 'Pulse',
  spin: 'Spin',
  shake: 'Shake',
};

export const ANIMATION_TIMING_OPTIONS = [
  'ease',
  'linear',
  'ease-in',
  'ease-out',
  'ease-in-out',
  'cubic-bezier(0.68, -0.55, 0.27, 1.55)',
];

export const ANIMATION_ITERATION_OPTIONS = ['1', '2', '3', 'infinite'];

export const ANIMATION_DIRECTION_OPTIONS = ['normal', 'reverse', 'alternate', 'alternate-reverse'];

export const DEFAULT_ANIMATION_STATE: AnimationState = {
  preset: 'fade',
  duration: 1,
  delay: 0,
  timing: 'ease',
  iteration: 'infinite',
  direction: 'normal',
};

// Returns the body of the @keyframes rule (the percentage stops) for a preset.
export function animationKeyframeBody(preset: AnimationPreset): string {
  switch (preset) {
    case 'fade':
      return `  0% { opacity: 0; }
  100% { opacity: 1; }`;
    case 'slide':
      return `  0% { transform: translateX(-120%); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }`;
    case 'bounce':
      return `  0%, 20%, 53%, 100% { transform: translateY(0); }
  40%, 43% { transform: translateY(-30px); }
  70% { transform: translateY(-15px); }
  90% { transform: translateY(-4px); }`;
    case 'pulse':
      return `  0% { transform: scale(1); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1); }`;
    case 'spin':
      return `  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }`;
    case 'shake':
      return `  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-8px); }
  40%, 80% { transform: translateX(8px); }`;
    default:
      return `  0% { opacity: 0; }
  100% { opacity: 1; }`;
  }
}

// Full @keyframes rule with the given animation name.
export function animationKeyframes(state: AnimationState, name: string): string {
  return `@keyframes ${name} {
${animationKeyframeBody(state.preset)}
}`;
}

// The animation shorthand value (without the `animation:` property prefix).
export function animationShorthandValue(state: AnimationState, name: string): string {
  return `${name} ${trimNum(state.duration)}s ${state.timing} ${trimNum(state.delay)}s ${state.iteration} ${state.direction}`;
}

// Full copyable CSS: keyframes + a rule using the animation shorthand.
export function animationCss(state: AnimationState): string {
  const name = `${state.preset}-anim`;
  return `${animationKeyframes(state, name)}

.animated-element {
  animation: ${animationShorthandValue(state, name)};
}`;
}

// --- css loader generator -------------------------------------------------

export type LoaderStyle = 'ring' | 'dots' | 'bars' | 'dual-ring' | 'pulse';

export type LoaderState = {
  style: LoaderStyle;
  color: string;
  size: number;
  speed: number;
};

export const LOADER_STYLES: LoaderStyle[] = ['ring', 'dots', 'bars', 'dual-ring', 'pulse'];

export const LOADER_STYLE_LABELS: Record<LoaderStyle, string> = {
  ring: 'Ring',
  dots: 'Bouncing dots',
  bars: 'Bars',
  'dual-ring': 'Dual ring',
  pulse: 'Pulse',
};

export const DEFAULT_LOADER_STATE: LoaderState = {
  style: 'ring',
  color: '#6366f1',
  size: 48,
  speed: 1,
};

// The HTML markup for a loader, using a class prefix so preview and copy can
// use different prefixes and never collide.
export function loaderHtml(state: LoaderState, prefix: string): string {
  switch (state.style) {
    case 'dots':
      return `<div class="${prefix}"><span></span><span></span><span></span></div>`;
    case 'bars':
      return `<div class="${prefix}"><span></span><span></span><span></span><span></span></div>`;
    case 'dual-ring':
      return `<div class="${prefix}"></div>`;
    case 'pulse':
      return `<div class="${prefix}"></div>`;
    case 'ring':
    default:
      return `<div class="${prefix}"></div>`;
  }
}

// The CSS rules + keyframes for a loader, scoped to the class prefix.
export function loaderCss(state: LoaderState, prefix: string): string {
  const { color, size, speed } = state;
  const s = `${size}px`;
  const dur = `${trimNum(speed)}s`;
  const anim = `${prefix}-spin`;
  const bounce = `${prefix}-bounce`;
  const stretch = `${prefix}-stretch`;
  const pulse = `${prefix}-pulse`;

  switch (state.style) {
    case 'dots': {
      const dot = Math.max(6, Math.round(size / 3));
      return `.${prefix} {
  display: inline-flex;
  gap: ${Math.round(dot / 2)}px;
}
.${prefix} span {
  width: ${dot}px;
  height: ${dot}px;
  border-radius: 50%;
  background: ${color};
  animation: ${bounce} ${dur} ease-in-out infinite;
}
.${prefix} span:nth-child(2) { animation-delay: ${trimNum(speed / 6)}s; }
.${prefix} span:nth-child(3) { animation-delay: ${trimNum(speed / 3)}s; }
@keyframes ${bounce} {
  0%, 80%, 100% { transform: scale(0.4); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}`;
    }
    case 'bars': {
      const bar = Math.max(4, Math.round(size / 6));
      return `.${prefix} {
  display: inline-flex;
  align-items: center;
  gap: ${Math.round(bar / 2)}px;
  height: ${s};
}
.${prefix} span {
  width: ${bar}px;
  height: 100%;
  background: ${color};
  border-radius: ${Math.round(bar / 2)}px;
  animation: ${stretch} ${dur} ease-in-out infinite;
}
.${prefix} span:nth-child(2) { animation-delay: ${trimNum(speed / 8)}s; }
.${prefix} span:nth-child(3) { animation-delay: ${trimNum(speed / 4)}s; }
.${prefix} span:nth-child(4) { animation-delay: ${trimNum((speed * 3) / 8)}s; }
@keyframes ${stretch} {
  0%, 40%, 100% { transform: scaleY(0.4); }
  20% { transform: scaleY(1); }
}`;
    }
    case 'dual-ring': {
      const border = Math.max(3, Math.round(size / 10));
      return `.${prefix} {
  width: ${s};
  height: ${s};
  border-radius: 50%;
  border: ${border}px solid ${color};
  border-color: ${color} transparent ${color} transparent;
  animation: ${anim} ${dur} linear infinite;
}
@keyframes ${anim} {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}`;
    }
    case 'pulse': {
      return `.${prefix} {
  width: ${s};
  height: ${s};
  border-radius: 50%;
  background: ${color};
  animation: ${pulse} ${dur} ease-in-out infinite;
}
@keyframes ${pulse} {
  0% { transform: scale(0.2); opacity: 1; }
  100% { transform: scale(1); opacity: 0; }
}`;
    }
    case 'ring':
    default: {
      const border = Math.max(3, Math.round(size / 8));
      return `.${prefix} {
  width: ${s};
  height: ${s};
  border-radius: 50%;
  border: ${border}px solid rgba(0, 0, 0, 0.12);
  border-top-color: ${color};
  animation: ${anim} ${dur} linear infinite;
}
@keyframes ${anim} {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}`;
    }
  }
}

// Combined copyable HTML + CSS snippet for the loader.
export function loaderSnippet(state: LoaderState): string {
  return `${loaderHtml(state, 'loader')}

<style>
${loaderCss(state, 'loader')}
</style>`;
}

// --- css text glitch effect generator -------------------------------------

export type GlitchState = {
  text: string;
  color: string;
  glitchA: string;
  glitchB: string;
  intensity: number;
};

export const DEFAULT_GLITCH_STATE: GlitchState = {
  text: 'GLITCH',
  color: '#f4f4f5',
  glitchA: '#00e5ff',
  glitchB: '#ff2965',
  intensity: 4,
};

// Escapes double quotes so the text can sit safely inside a data attribute.
function escapeAttr(value: string): string {
  return value.replace(/"/g, '&quot;');
}

export function glitchHtml(state: GlitchState, prefix: string): string {
  const safe = escapeAttr(state.text);
  return `<span class="${prefix}" data-text="${safe}">${safe}</span>`;
}

export function glitchCss(state: GlitchState, prefix: string): string {
  const { color, glitchA, glitchB, intensity } = state;
  const shift = Math.max(1, intensity);
  const a = `${prefix}-a`;
  const b = `${prefix}-b`;
  return `.${prefix} {
  position: relative;
  display: inline-block;
  color: ${color};
  font-weight: 800;
  letter-spacing: 2px;
}
.${prefix}::before,
.${prefix}::after {
  content: attr(data-text);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  overflow: hidden;
}
.${prefix}::before {
  color: ${glitchA};
  animation: ${a} ${trimNum(2.6 - intensity * 0.15)}s infinite linear alternate-reverse;
}
.${prefix}::after {
  color: ${glitchB};
  animation: ${b} ${trimNum(1.8 - intensity * 0.1)}s infinite linear alternate-reverse;
}
@keyframes ${a} {
  0% { clip-path: inset(10% 0 85% 0); transform: translateX(-${shift}px); }
  25% { clip-path: inset(70% 0 5% 0); transform: translateX(${shift}px); }
  50% { clip-path: inset(30% 0 45% 0); transform: translateX(-${shift}px); }
  75% { clip-path: inset(55% 0 25% 0); transform: translateX(${shift}px); }
  100% { clip-path: inset(15% 0 70% 0); transform: translateX(-${shift}px); }
}
@keyframes ${b} {
  0% { clip-path: inset(65% 0 15% 0); transform: translateX(${shift}px); }
  25% { clip-path: inset(20% 0 60% 0); transform: translateX(-${shift}px); }
  50% { clip-path: inset(80% 0 5% 0); transform: translateX(${shift}px); }
  75% { clip-path: inset(35% 0 40% 0); transform: translateX(-${shift}px); }
  100% { clip-path: inset(50% 0 30% 0); transform: translateX(${shift}px); }
}`;
}

export function glitchSnippet(state: GlitchState): string {
  return `${glitchHtml(state, 'glitch')}

<style>
${glitchCss(state, 'glitch')}
</style>`;
}

// --- svg stroke-to-fill converter -----------------------------------------

export type StrokeToFillResult = {
  output: string;
  notes: string[];
  changed: number;
};

const SHAPE_TAGS = new Set([
  'path',
  'circle',
  'rect',
  'line',
  'polyline',
  'polygon',
  'ellipse',
  'g',
  'use',
]);

// Parses `key="value"` / `key='value'` pairs from an attribute string in order.
function parseAttributes(attrText: string): { name: string; value: string; quote: string }[] {
  const attrs: { name: string; value: string; quote: string }[] = [];
  const re = /([a-zA-Z_:][\w:.-]*)\s*=\s*("([^"]*)"|'([^']*)')/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(attrText)) !== null) {
    const quote = match[2].startsWith('"') ? '"' : "'";
    attrs.push({ name: match[1], value: match[3] ?? match[4] ?? '', quote });
  }
  return attrs;
}

function serializeAttributes(attrs: { name: string; value: string; quote: string }[]): string {
  if (attrs.length === 0) return '';
  return ` ${attrs.map((a) => `${a.name}=${a.quote}${a.value}${a.quote}`).join(' ')}`;
}

// Reads a `stroke` color out of an inline style="" string, if present.
function strokeFromStyle(style: string): string | null {
  const match = /(?:^|;)\s*stroke\s*:\s*([^;]+)/i.exec(style);
  return match ? match[1].trim() : null;
}

function stripStrokeFromStyle(style: string): string {
  return style
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part.length > 0 && !/^stroke(-[\w-]+)?\s*:/i.test(part))
    .join('; ');
}

// Practical stroke -> fill transform: for every shape element that paints a
// stroke, copy that color into `fill` and drop the stroke-* attributes. This is
// NOT a true geometric outline (that needs path-offset math); it is the common,
// useful case of "this icon was drawn with strokes, I want solid fills".
export function convertStrokeToFill(svg: string): StrokeToFillResult {
  const notes: string[] = [];
  let changed = 0;

  if (!svg.trim()) {
    return { output: '', notes: ['Paste some SVG markup to convert.'], changed: 0 };
  }
  if (!/<svg[\s>]/i.test(svg)) {
    return {
      output: svg,
      notes: ['No <svg> element was found in the pasted markup.'],
      changed: 0,
    };
  }

  const tagRe = /<([a-zA-Z][\w:-]*)((?:[^>"']|"[^"]*"|'[^']*')*?)(\/?)>/g;

  const output = svg.replace(
    tagRe,
    (whole, tagName: string, attrText: string, selfClose: string) => {
      const tag = tagName.toLowerCase();
      if (!SHAPE_TAGS.has(tag)) return whole;

      const attrs = parseAttributes(attrText);
      const strokeAttr = attrs.find((a) => a.name.toLowerCase() === 'stroke');
      const styleAttr = attrs.find((a) => a.name.toLowerCase() === 'style');
      const styleStroke = styleAttr ? strokeFromStyle(styleAttr.value) : null;

      const strokeColor = (strokeAttr?.value ?? styleStroke ?? '').trim();
      const hasStroke = strokeColor !== '' && strokeColor.toLowerCase() !== 'none';
      if (!hasStroke) return whole;

      changed += 1;

      // Remove stroke-* presentation attributes.
      const kept = attrs.filter((a) => !/^stroke(-[\w-]+)?$/i.test(a.name));

      // Set / override fill with the stroke color.
      const fillAttr = kept.find((a) => a.name.toLowerCase() === 'fill');
      if (fillAttr) {
        fillAttr.value = strokeColor;
      } else {
        kept.unshift({ name: 'fill', value: strokeColor, quote: '"' });
      }

      // Clean stroke declarations out of any inline style.
      const keptStyle = kept.find((a) => a.name.toLowerCase() === 'style');
      if (keptStyle) {
        keptStyle.value = stripStrokeFromStyle(keptStyle.value);
        if (keptStyle.value === '') {
          const idx = kept.indexOf(keptStyle);
          kept.splice(idx, 1);
        }
      }

      return `<${tagName}${serializeAttributes(kept)}${selfClose}>`;
    },
  );

  if (changed === 0) {
    notes.push('No stroked shapes were found, so nothing was changed.');
  } else {
    notes.push(
      `Converted ${changed} stroked shape${changed === 1 ? '' : 's'} to filled equivalents.`,
    );
    notes.push(
      'This copies each stroke color into fill and removes stroke-* attributes. For thin outlines this reads well; a true outline (preserving line thickness as a shape) needs path-offset math and is not attempted.',
    );
  }

  return { output, notes, changed };
}
